/**
 * Firestore service for card master data (Scryfall cards)
 */
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where, type QueryConstraint } from 'firebase/firestore';

import type { MatchedImportCard } from '@/features/import-export/types';
import { firestore } from '@/services/firebase';
import type { ScryfallCard } from '@/types/card';
import type { ImportCard } from '@/types/schemas';

const CARDS_COLLECTION = 'cards';

/**
 * Get a single card by Scryfall ID
 */
export async function getCardById(scryfallId: string): Promise<ScryfallCard | null> {
  const docRef = doc(firestore, CARDS_COLLECTION, scryfallId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return { id: snapshot.id, ...snapshot.data() } as ScryfallCard;
}

/**
 * Get multiple cards by Scryfall IDs
 */
export async function getCardsByIds(scryfallIds: string[]): Promise<Map<string, ScryfallCard>> {
  const cards = new Map<string, ScryfallCard>();

  // Firestore 'in' queries are limited to 30 items
  const chunks: string[][] = [];
  for (let i = 0; i < scryfallIds.length; i += 30) {
    chunks.push(scryfallIds.slice(i, i + 30));
  }

  for (const chunk of chunks) {
    const q = query(collection(firestore, CARDS_COLLECTION), where('__name__', 'in', chunk));
    const snapshot = await getDocs(q);

    snapshot.docs.forEach((docSnap) => {
      cards.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as ScryfallCard);
    });
  }

  return cards;
}

/**
 * Find a card by set code and collector number
 */
export async function findCardBySetAndNumber(setCode: string, collectorNumber: string): Promise<ScryfallCard | null> {
  const q = query(
    collection(firestore, CARDS_COLLECTION),
    where('set', '==', setCode.toLowerCase()),
    where('collector_number', '==', collectorNumber),
    limit(1),
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() } as ScryfallCard;
}

/**
 * Search cards by name (case-insensitive prefix match)
 */
export async function searchCardsByName(searchTerm: string, maxResults: number = 50): Promise<ScryfallCard[]> {
  const lowerSearch = searchTerm.toLowerCase();
  const endSearch = lowerSearch + '\uf8ff'; // Unicode high character for range query

  const q = query(
    collection(firestore, CARDS_COLLECTION),
    where('name_lower', '>=', lowerSearch),
    where('name_lower', '<=', endSearch),
    orderBy('name_lower'),
    limit(maxResults),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as ScryfallCard);
}

/**
 * Get all cards (for initial load / client-side filtering)
 * Note: This fetches ALL cards - use sparingly and cache results
 */
export async function getAllCards(): Promise<ScryfallCard[]> {
  const snapshot = await getDocs(collection(firestore, CARDS_COLLECTION));
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as ScryfallCard);
}

/**
 * Query cards with filters
 */
export async function queryCards(options: {
  set?: string;
  rarity?: string;
  colors?: string[];
  maxResults?: number;
}): Promise<ScryfallCard[]> {
  const constraints: QueryConstraint[] = [];

  if (options.set) {
    constraints.push(where('set', '==', options.set.toLowerCase()));
  }

  if (options.rarity) {
    constraints.push(where('rarity', '==', options.rarity));
  }

  if (options.colors && options.colors.length > 0) {
    constraints.push(where('color_identity', 'array-contains-any', options.colors));
  }

  constraints.push(orderBy('name_lower'));

  if (options.maxResults) {
    constraints.push(limit(options.maxResults));
  }

  const q = query(collection(firestore, CARDS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as ScryfallCard);
}

/**
 * Find card by exact name match
 */
export async function findCardByName(name: string): Promise<ScryfallCard | null> {
  const lowerName = name.toLowerCase();

  const q = query(collection(firestore, CARDS_COLLECTION), where('name_lower', '==', lowerName), limit(1));

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() } as ScryfallCard;
}

/**
 * Match import cards to Scryfall database
 * Returns cards with match status and matched Scryfall card data
 */
export async function matchCardsToScryfall(cards: ImportCard[]): Promise<MatchedImportCard[]> {
  const results: MatchedImportCard[] = [];

  for (const card of cards) {
    let matched: ScryfallCard | null = null;
    let matchType: 'exact' | 'fuzzy' | 'none' = 'none';

    // Try exact match by set + collector number first
    if (card.setCode && card.collectorNumber) {
      matched = await findCardBySetAndNumber(card.setCode, card.collectorNumber);
      if (matched) {
        matchType = 'exact';
      }
    }

    // Fallback to name search
    if (!matched) {
      matched = await findCardByName(card.name);
      if (matched) {
        // Check if it's a fuzzy match (name differs slightly)
        matchType = matched.name.toLowerCase() === card.name.toLowerCase() ? 'exact' : 'fuzzy';
      }
    }

    // Fuzzy search as last resort
    if (!matched) {
      const nameMatches = await searchCardsByName(card.name, 1);
      if (nameMatches.length > 0) {
        matched = nameMatches[0];
        matchType = 'fuzzy';
      }
    }

    results.push({
      ...card,
      matched: matched ?? null,
      matchType,
      error: matched ? undefined : 'Card not found in database',
    });
  }

  return results;
}
